import { FC, createContext, useContext, useState, ReactNode, useCallback } from 'react';

import { RuleServiceCommand } from '../../../services/command';
import { getRegionId } from '../helper/RuleServiceV2.helper';

const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();
interface RuleServiceContextType {
    tenant: string;
    displayGlobalRules: boolean;
    setDisplayGlobalRules: (displayGlobalRules: boolean) => void;
    setTenant: (tenant: string) => void;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    page: number;
    setPage: (page: number) => void;
    totalCount: number;
    setTotalCount: (totalCount: number) => void;
    activeTab: number;
    setActiveTab: (totalCount: number) => void;
    gridData: any[];
    setGridData: (gridData: any[]) => void;
    clearRuleData: () => void;
    fetchRuleTemplateGridData: () => void;
    fetchCriteriaTypeGridData: () => void;
    fetchRuleGridData: () => void;
}

const RuleServiceContext = createContext<RuleServiceContextType | undefined>(undefined);

export const RuleServiceProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [tenant, setTenant] = useState<string>('');
    const [displayGlobalRules, setDisplayGlobalRules] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [gridData, setGridData] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<number>(0);

    const clearRuleData = (clearTenant?: boolean) => {
        if (clearTenant) setTenant('');
        setIsLoading(false);
        setRowsPerPage(10);
        setPage(0);
        setTotalCount(0);
        setGridData([]);
    };

    const fetchRuleTemplateGridData = useCallback(() => {
        if ((tenant != undefined || tenant != null) && typeof rowsPerPage === 'number' && typeof page === 'number') {
            setIsLoading(true);
            RuleServiceCommand.GetRuleTemplateList({
                Tenant: tenant,
                Take: rowsPerPage,
                Skip: page,
            })
                .then((ruleTemplates: any) => {
                    setGridData(ruleTemplates.data);
                    setTotalCount(ruleTemplates.count);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [tenant, rowsPerPage, page]);

    const fetchCriteriaTypeGridData = useCallback(() => {
        if ((tenant != undefined || tenant != null) && typeof rowsPerPage === 'number' && typeof page === 'number') {
            setIsLoading(true);
            RuleServiceCommand.GetCriteriaTypeList({
                Tenant: tenant,
                Take: rowsPerPage,
                Skip: page,
            })
                .then((response) => {
                    setGridData(response.data);
                    setTotalCount(response.count);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [tenant, rowsPerPage, page]);

    const fetchRuleGridData = useCallback(() => {
        if ((tenant != undefined || tenant != null) && typeof rowsPerPage === 'number' && typeof page === 'number') {
            setIsLoading(true);
            RuleServiceCommand.GetRuleList({
                Tenant: tenant,
                RegionId: getRegionId(),
                countryId: displayGlobalRules ? 0 : countryId,
                Status: 1,
                Take: rowsPerPage,
                Skip: page,
            })
                .then((response) => {
                    setGridData(response.data);
                    setTotalCount(response.count);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [tenant, rowsPerPage, page]);

    return (
        <RuleServiceContext.Provider
            value={{
                tenant,
                displayGlobalRules,
                setDisplayGlobalRules,
                setTenant,
                isLoading,
                setIsLoading,
                gridData,
                setGridData,
                totalCount,
                setTotalCount,
                page,
                setPage,
                rowsPerPage,
                setRowsPerPage,
                clearRuleData,
                activeTab,
                setActiveTab,
                fetchRuleTemplateGridData,
                fetchCriteriaTypeGridData,
                fetchRuleGridData,
            }}
        >
            {children}
        </RuleServiceContext.Provider>
    );
};

export const useRuleService = () => {
    const context = useContext(RuleServiceContext);
    if (!context) {
        throw new Error('useRuleService must be used within a RuleServiceProvider');
    }
    return context;
};
